import { useEffect, useState } from "react";
import { documentAdminService } from "../../services/document-admin.service";

const ETL_URL =
  import.meta.env.VITE_API_URL_ETL || "http://localhost:8788";

interface SignatureInfoModalProps {
  documentId: string;
  documentName: string;
  onClose: () => void;
}

export function SignatureInfoModal({
  documentId,
  documentName,
  onClose,
}: SignatureInfoModalProps) {
  const [info, setInfo] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await documentAdminService.getSignatureInfo(documentId);
        setInfo(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [documentId]);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              Detalles de firma
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {documentName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
            {error}
          </div>
        ) : info && !info.signed ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-2 opacity-40">📝</div>
            <p className="text-slate-600">Este documento aún no ha sido firmado.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-xs text-green-700 font-medium mb-1">
                Firmado por
              </p>
              <p className="font-bold text-green-900">
                {info.signedByName || "—"}
              </p>
              {info.signedByEmail && (
                <p className="text-xs text-green-700">{info.signedByEmail}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Fecha y hora</p>
                <p className="font-medium text-slate-900">
                  {info.signedAt
                    ? new Date(info.signedAt).toLocaleString("es-MX")
                    : "—"}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">IP</p>
                <p className="font-mono text-xs text-slate-900 break-all">
                  {info.signedIp || "—"}
                </p>
              </div>
            </div>

            {info.signedName && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-xs text-slate-500">Nombre declarado</p>
                <p className="font-medium text-slate-900">{info.signedName}</p>
              </div>
            )}

            {info.signedUserAgent && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-xs text-slate-500">Navegador / dispositivo</p>
                <p className="text-xs text-slate-700 break-all">
                  {info.signedUserAgent}
                </p>
              </div>
            )}

            {info.signed && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Firma registrada</p>
                <img
                  src={`${ETL_URL}/api/public/photos/signatures/${documentId}`}
                  alt="Firma"
                  className="bg-white rounded border border-slate-200 max-h-[120px]"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}